import { ServiceAnna, ServiceFeatures } from '@mirai/services';

import { METRICS_EVENTS } from '../components/helpers';

const ERROR_TRACKING_FEATURE = 'errorTracking';
const MAX_REPORTS_PER_MESSAGE = 5;
const MAX_TRACKED_MESSAGES = 100;
const RATE_LIMIT_WINDOW_MS = 60000;

const resolveBase = (url) => {
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${pathname.replace(/[^/]*$/, '')}`;
  } catch {
    return undefined;
  }
};

const CORE_BASE =
  resolveBase(new Error().stack?.match(/https?:\/\/[^\s)'"]+/)?.[0]) ||
  resolveBase(process.env.SERVICE_STATIC && `${process.env.SERVICE_STATIC}/core/index.js`);

const reportedErrors = new WeakMap();
const messageCounts = new Map();
let errorContext = {};
let removeGlobalErrorListeners;

const isErrorTrackingEnabled = () => {
  const id = errorContext.chain || errorContext.hotel;
  return !!id && ServiceFeatures.get(ERROR_TRACKING_FEATURE, id);
};

const isCoreUrl = (url) => !!CORE_BASE && typeof url === 'string' && url.startsWith(CORE_BASE);

const isStackFrame = (line) => /^\s*at\s/.test(line) || line.includes('@');

const stackHasCoreFrame = (stack) =>
  typeof stack === 'string' &&
  stack.split('\n').some((line) => isStackFrame(line) && isCoreUrl((line.match(/https?:\/\/[^\s)'"]+/) || [])[0]));

const isCoreSource = ({ filename, stack } = {}) => (filename ? isCoreUrl(filename) : stackHasCoreFrame(stack));

const isDuplicate = (error, now) => {
  if (error === null || (typeof error !== 'object' && typeof error !== 'function')) return false;
  const lastReported = reportedErrors.get(error);
  if (lastReported && now - lastReported <= RATE_LIMIT_WINDOW_MS) return true;
  reportedErrors.set(error, now);
  return false;
};

const isRateLimited = (message, now) => {
  const entry = messageCounts.get(message);
  if (entry && now - entry.since <= RATE_LIMIT_WINDOW_MS) {
    if (entry.count >= MAX_REPORTS_PER_MESSAGE) return true;
    entry.count += 1;
    return false;
  }
  if (!entry && messageCounts.size >= MAX_TRACKED_MESSAGES) messageCounts.delete(messageCounts.keys().next().value);
  messageCounts.set(message, { count: 1, since: now });
  return false;
};

export const configureErrorTracking = ({ hotel, id, session, type } = {}) => {
  errorContext = {
    chain: type === 'chain' ? id : undefined,
    hotel: hotel?.id,
    session,
  };
};

export const reportError = (error, { componentStack } = {}) => {
  const now = Date.now();
  if (isDuplicate(error, now)) return;

  const errorMessage = error?.message || (typeof error === 'string' ? error : String(error ?? 'Unknown error'));

  if (isRateLimited(errorMessage, now)) return;

  const errorStack = [error?.stack, componentStack].filter(Boolean).join('\n') || undefined;
  trackError(errorMessage, errorStack);
};

const handleError = ({ error, message, filename } = {}) => {
  if (!isCoreSource({ filename, stack: error?.stack })) return;
  reportError(error ?? message);
};

const handleUnhandledRejection = ({ reason } = {}) => {
  if (!isCoreSource({ stack: reason?.stack })) return;
  reportError(reason);
};

export const initializeErrorTracking = () => {
  if (removeGlobalErrorListeners) return removeGlobalErrorListeners;

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  removeGlobalErrorListeners = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    removeGlobalErrorListeners = undefined;
  };

  return removeGlobalErrorListeners;
};

export const trackError = (errorMessage, errorStack, overrideTrakingEnabled) => {
  if (typeof errorMessage !== 'string' || errorMessage.trim() === '') return;
  if (
    window.IS_PLAYWRIGHT ||
    window.location.host === 'monalisa.mirai.com' ||
    (!isErrorTrackingEnabled() && !overrideTrakingEnabled)
  )
    return;

  ServiceAnna.track({
    ...errorContext,
    errorMessage,
    errorStack: errorStack
      ? typeof errorStack === 'object'
        ? JSON.stringify(errorStack)
        : String(errorStack)
      : undefined,
    eventName: METRICS_EVENTS.ERROR_CORE,
  });
};
